import { Route, Routes, useLocation, useNavigate } from 'react-router';
import MainLayout from './layout/MainLayout.tsx';
import { useAppSelector } from './redux/store.ts';
import AuthRoute from './routes/AuthRoute.tsx';
import FrameworkRoute from './routes/FrameworkRoute';
import ServiceRoute from './routes/ServiceRoute.tsx';
import Loader from './views/common/loader/Loader.tsx';
import useTokenManagement from './hooks/useTokenManagement.ts';
import useScreenSize from './hooks/useScreenSize.ts';
import { DOCS_URL, MOBILE_BREAKPOINT } from './constants.ts';
import { lazy, Suspense } from 'react';
import TermsWrapper from './views/common/terms/TermsWrapper.tsx';
import meta from './meta.ts';
import { useTranslation } from 'react-i18next';
import DocumentationProvider from './views/Documentation/context/DocumentationContext.tsx';
import NotFound from "@/layout/NotFound.tsx";

const Swagger = lazy(() => import('./swagger/DevUI.tsx'));
const DocumentationPage = lazy(() => import('./views/Documentation/DocPageUI.tsx'));
const LoginFailed = lazy(() => import('./views/login/LoginFailed.tsx'));
const AcceptedTermsMobile = lazy(() => import('./views/common/terms/AcceptedTermsMobile.tsx'));
const FetchError = lazy(() => import('./views/common/errorPage/FetchError.tsx'));
const SelectAccount = lazy(() => import('./views/accounts/SelectAccount.tsx'));
const CreateAccount = lazy(() => import('./views/accounts/CreateAccount.tsx'));
const Home = lazy(() => import('./views/home/Home.tsx'));
const Redirect = lazy(() => import('./views/login/Redirect.tsx'));
const RemoteApp = lazy(() => import('./views/remoteApp/RemoteApp.tsx'));

function App() {
  useTokenManagement();
  const { servicesList: services, publicServicesList, init } = useAppSelector(state => state.layout);
  const { width } = useScreenSize();
  const { i18n } = useTranslation();

  if (init) {
    return (
      <div data-testid="loader-container" className="framework-main-content">
        <Loader />
      </div>
    );
  }

  const publicServices = publicServicesList.filter(service => !service.path.startsWith('http'));

  return (
    <DocumentationProvider
      value={{
        fallbackLanguage: 'en',
        currentLanguage: i18n.language,
        docRoute: 'docs',
        basePath: `${DOCS_URL}/docs`,
        modulePath: '',
        navigateFn: useNavigate,
        locationFn: useLocation,
        meta: meta.docs,
      }}
    >
      <Suspense fallback={<Loader />}>
        <Routes>
          <Route key="redirect" path="/redirect" element={<Redirect />} />
          <Route element={<FrameworkRoute />}>
            <Route path="/" element={<MainLayout />}>
              <Route key="login-failed" path="/login-failed" element={<LoginFailed />} />
              <Route key="/123" index element={<Home />} />
              {publicServices?.map(service => (
                <Route
                  key={service.path}
                  path={`${service.path}/*`}
                  element={
                    <ServiceRoute service={service}>
                      <RemoteApp service={service} />
                    </ServiceRoute>
                  }
                />
              ))}
              <Route element={<AuthRoute />}>
                <Route key="select-account" path="/select-account" element={<SelectAccount />} />
                <Route key="create-account" path="/create-account" element={<CreateAccount />} />
                {services?.map(service => (
                  <Route
                    key={service.path}
                    path={`${service.path}/*`}
                    element={
                      <ServiceRoute service={service}>
                        <RemoteApp service={service} />
                      </ServiceRoute>
                    }
                  />
                ))}
                <Route key="fail" path="/fetch-error" element={<FetchError />} />
                <Route
                  key="terms"
                  path="/terms"
                  element={
                    width > MOBILE_BREAKPOINT ? (
                      <Suspense fallback={<Loader />}>
                        {' '}
                        <TermsWrapper />
                      </Suspense>
                    ) : (
                      <AcceptedTermsMobile />
                    )
                  }
                />
                <Route
                  path="/docs/*"
                  element={
                    <Suspense fallback={<Loader />}>
                      {' '}
                      <DocumentationPage />{' '}
                    </Suspense>
                  }
                />
                <Route
                  path="/development/*"
                  element={
                    <Suspense fallback={<Loader />}>
                      {' '}
                      <Swagger />{' '}
                    </Suspense>
                  }
                />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </DocumentationProvider>
  );
}

export default App;
